import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@/lib/zodcompat";
import { z } from "zod";
import { Dialog } from "@base-ui/react/dialog";
import { MoneyInput } from "@/components/form/money-input";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useCreateInvoicePayment } from "@/hooks/useInvoices";
import { useCashAccounts } from "@/hooks/useCashAccounts";
import { cn } from "@/lib/utils";

const PAYMENT_METHODS = [
  { value: "PIX", label: "PIX" },
  { value: "BANK_TRANSFER", label: "Transferência bancária" },
  { value: "BANK_SLIP", label: "Boleto" },
  { value: "CASH", label: "Dinheiro" },
  { value: "OTHER", label: "Outro" },
] as const;

const selectClass = cn(
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

function makeSchema(maxAmount: number) {
  return z.object({
    amount: z.coerce
      .number({ error: "Valor inválido" })
      .positive("Valor deve ser positivo")
      .max(maxAmount + 0.001, `Valor excede o saldo de R$ ${maxAmount.toFixed(2)}`),
    paymentDate: z.string().min(1, "Data obrigatória"),
    method: z.enum(["PIX", "BANK_TRANSFER", "BANK_SLIP", "CASH", "OTHER"]),
    notes: z.string().optional(),
    accountId: z.string().min(1, "Conta obrigatória"),
  });
}

type PaymentFormData = {
  amount: number;
  paymentDate: string;
  method: "PIX" | "BANK_TRANSFER" | "BANK_SLIP" | "CASH" | "OTHER";
  notes?: string;
  accountId: string;
};

interface PaymentModalProps {
  invoiceId: string;
  remainingAmount: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function PaymentModal({
  invoiceId,
  remainingAmount,
  open,
  onClose,
  onSuccess,
}: PaymentModalProps) {
  const { mutate: createPayment, isPending } = useCreateInvoicePayment(invoiceId);
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 });
  const accounts = accountsData?.data ?? [];

  const schema = makeSchema(remainingAmount);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PaymentFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: "" as unknown as number,
      paymentDate: todayISO(),
      method: "PIX",
      notes: "",
      accountId: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        amount: remainingAmount > 0 ? (remainingAmount as unknown as number) : ("" as unknown as number),
        paymentDate: todayISO(),
        method: "PIX",
        notes: "",
        accountId: "",
      });
    }
  }, [open, remainingAmount, reset]);

  useEffect(() => {
    if (open && accounts.length > 0) {
      const fallback = accounts.find((a) => a.isDefault)?.id ?? accounts[0]!.id;
      setValue("accountId", fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountsData]);

  const amount = watch("amount") || 0;
  const afterPayment = remainingAmount - amount;

  function onSubmit(data: PaymentFormData) {
    createPayment(
      {
        amount: data.amount,
        paymentDate: new Date(data.paymentDate).toISOString(),
        method: data.method,
        notes: data.notes || undefined,
        accountId: data.accountId,
      },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Registrar Pagamento
          </Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Saldo devedor: <span className="font-medium text-foreground">{formatMoney(remainingAmount)}</span>
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="amount">Valor *</Label>
                <MoneyInput
                  id="amount"
                  error={!!errors.amount}
                  {...register("amount")}
                />
                {errors.amount && (
                  <p className="text-xs text-destructive">{errors.amount.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="paymentDate">Data *</Label>
                <Input
                  id="paymentDate"
                  type="date"
                  aria-invalid={!!errors.paymentDate}
                  {...register("paymentDate")}
                />
                {errors.paymentDate && (
                  <p className="text-xs text-destructive">{errors.paymentDate.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="method">Forma de pagamento *</Label>
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <select
                    id="method"
                    value={field.value}
                    onChange={field.onChange}
                    className={selectClass}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="accountId">Conta de recebimento *</Label>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <select
                    id="accountId"
                    value={field.value}
                    onChange={field.onChange}
                    className={selectClass}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.accountId && (
                <p className="text-xs text-destructive">{errors.accountId.message}</p>
              )}
              {accounts.length === 0 && (
                <p className="text-xs text-destructive">
                  Cadastre uma conta de caixa antes de registrar pagamentos.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Observações</Label>
              <Input id="notes" {...register("notes")} />
            </div>

            {amount > 0 && (
              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
                <p className="text-xs text-muted-foreground mb-1">Saldo após pagamento</p>
                <p className={cn(
                  "font-semibold",
                  afterPayment <= 0.001 ? "text-green-700" : "text-foreground",
                )}>
                  {afterPayment <= 0.001 ? "Quitado" : formatMoney(afterPayment)}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={isPending} loadingText="Registrando…">
                Registrar
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
